import React, { useState, useEffect } from 'react';
import { Plug, RefreshCw, Eye, CheckCircle2, XCircle, Loader2, Clock, CalendarClock } from 'lucide-react';
import C from '../../constants/colors';
import * as odooApi from '../../api/odoo';

function timeAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60)    return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)    return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)    return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function IntegrationsView({ onSyncComplete }) {
  const [status, setStatus]         = useState(null); // { configured, reachable, url, db, error }
  const [statusLoading, setStatusL] = useState(true);
  const [preview, setPreview]       = useState(null); // { companies, employeeCount, employees }
  const [previewLoading, setPL]     = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [syncResult, setSyncResult] = useState(null); // stats from /sync
  const [errorMsg, setErrorMsg]     = useState('');
  const [autoSyncSaving, setAutoS]  = useState(false);

  useEffect(() => { loadStatus(); }, []);

  async function loadStatus() {
    setStatusL(true);
    try { setStatus(await odooApi.status()); }
    catch (err) { setStatus({ configured: false, error: err.response?.data?.error || err.message }); }
    finally { setStatusL(false); }
  }

  async function handlePreview() {
    setPL(true);
    setErrorMsg('');
    try { setPreview(await odooApi.preview()); }
    catch (err) { setErrorMsg(err.response?.data?.error || 'Preview failed'); }
    finally { setPL(false); }
  }

  async function handleAutoSyncToggle() {
    if (!status) return;
    const next = !status.autoSyncEnabled;
    setAutoS(true);
    setErrorMsg('');
    try {
      await odooApi.setAutoSync(next);
      setStatus(p => ({ ...p, autoSyncEnabled: next }));
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Failed to update auto-sync');
    } finally {
      setAutoS(false);
    }
  }

  async function handleSync() {
    if (!window.confirm('Sync employees from Odoo? This will create/update companies and employees in SafeCheck.')) return;
    setSyncing(true);
    setErrorMsg('');
    setSyncResult(null);
    try {
      const result = await odooApi.sync();
      setSyncResult(result);
      if (onSyncComplete) await onSyncComplete();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  const statusColor = !status?.configured ? C.textMuted : (status.reachable ? C.green : C.red);
  const statusLabel = statusLoading ? 'Checking…'
    : !status?.configured ? 'Not configured'
    : status.reachable ? 'Connected'
    : 'Unreachable';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Plug size={20} color={C.blue} />Integrations
        </h2>
        <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '6px' }}>
          Connect external systems to SafeCheck. Sync data from your HR system or other sources.
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {/* ── Odoo card ── */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px 20px', maxWidth: '760px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#714B67', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '13px' }}>O</div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary }}>Odoo HR</div>
                <div style={{ fontSize: '11px', color: C.textMuted }}>One-way sync — companies and employees</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: statusColor, fontWeight: 600 }}>
              {statusLoading ? <Loader2 size={13} className="spin" /> : (status?.reachable ? <CheckCircle2 size={13} /> : <XCircle size={13} />)}
              {statusLabel}
            </div>
          </div>

          {status?.configured && status.url && (
            <div style={{ fontSize: '12px', color: C.textSecondary, marginBottom: '14px', padding: '10px 12px', background: C.surfaceElevated, borderRadius: '7px', border: `1px solid ${C.border}` }}>
              <div><span style={{ color: C.textMuted }}>URL: </span>{status.url}</div>
              {status.db && <div><span style={{ color: C.textMuted }}>Database: </span>{status.db}</div>}
              {status.error && <div style={{ color: C.red, marginTop: '4px' }}>{status.error}</div>}
            </div>
          )}

          {status?.lastRun && (
            <div style={{ fontSize: '12px', color: C.textSecondary, marginBottom: '14px', padding: '10px 12px', background: C.surfaceElevated, borderRadius: '7px', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Clock size={13} color={status.lastRun.status === 'success' ? C.green : C.red} />
              <div style={{ flex: 1 }}>
                <div>
                  <span style={{ color: C.textMuted }}>Last sync: </span>
                  <span style={{ color: C.textPrimary, fontWeight: 600 }}>{timeAgo(status.lastRun.completedAt || status.lastRun.startedAt)}</span>
                  <span style={{ color: C.textMuted }}> · {status.lastRun.triggeredBy === 'cron' ? 'auto' : 'manual'} · {status.lastRun.status}</span>
                </div>
                {status.lastRun.status === 'error' && status.lastRun.errorMessage && (
                  <div style={{ color: C.red, marginTop: '2px', fontSize: '11px' }}>{status.lastRun.errorMessage}</div>
                )}
              </div>
            </div>
          )}

          {status?.configured && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', padding: '12px 14px', background: C.surfaceElevated, borderRadius: '7px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
                <CalendarClock size={14} color={status.autoSyncEnabled ? C.blue : C.textMuted} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Schedule sync overnight</div>
                  <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                    {status.autoSyncEnabled
                      ? <>Runs daily at <b style={{ color: C.textSecondary }}>{String(status.autoSyncHourUtc).padStart(2,'0')}:00 UTC</b>. You can still run a manual sync any time.</>
                      : <>Manual only — sync runs only when you click the button.</>}
                  </div>
                </div>
              </div>
              <div onClick={autoSyncSaving ? undefined : handleAutoSyncToggle}
                style={{ width: '42px', height: '24px', borderRadius: '12px', background: status.autoSyncEnabled ? C.blue : C.border, cursor: autoSyncSaving ? 'default' : 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '14px', opacity: autoSyncSaving ? 0.6 : 1 }}>
                <div style={{ position: 'absolute', top: '3px', left: status.autoSyncEnabled ? '21px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
              </div>
            </div>
          )}

          {!status?.configured && !statusLoading && (
            <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px' }}>
              Set <code style={{ color: C.textSecondary }}>ODOO_URL</code>, <code style={{ color: C.textSecondary }}>ODOO_DB</code>, <code style={{ color: C.textSecondary }}>ODOO_USER</code> and <code style={{ color: C.textSecondary }}>ODOO_PASSWORD</code> in the backend environment to enable.
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={handlePreview} disabled={!status?.reachable || previewLoading || syncing}
              style={btnSecondary(!status?.reachable || previewLoading || syncing)}>
              {previewLoading ? <Loader2 size={13} className="spin" /> : <Eye size={13} />}
              {previewLoading ? 'Loading…' : 'Preview'}
            </button>
            <button onClick={handleSync} disabled={!status?.reachable || syncing || previewLoading}
              style={btnPrimary(!status?.reachable || syncing || previewLoading)}>
              {syncing ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
              {syncing ? 'Syncing…' : 'Run Sync'}
            </button>
            <button onClick={loadStatus} disabled={statusLoading}
              style={btnGhost(statusLoading)}>
              Test Connection
            </button>
          </div>

          {errorMsg && (
            <div style={{ marginTop: '12px', padding: '10px 12px', background: C.red+'18', border: `1px solid ${C.red}55`, borderRadius: '7px', color: C.red, fontSize: '12px' }}>
              {errorMsg}
            </div>
          )}

          {syncResult && (
            <div style={{ marginTop: '12px', padding: '12px 14px', background: C.green+'18', border: `1px solid ${C.green}55`, borderRadius: '7px', fontSize: '12px', color: C.textPrimary }}>
              <div style={{ fontWeight: 700, color: C.green, marginBottom: '6px' }}>Sync complete</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '6px 14px' }}>
                <span><b>{syncResult.companiesCreated}</b> companies created</span>
                <span><b>{syncResult.companiesExisting}</b> companies already linked</span>
                <span><b>{syncResult.employeesCreated}</b> employees created</span>
                <span><b>{syncResult.employeesUpdated}</b> employees updated</span>
                {syncResult.employeesSkipped > 0 && <span><b>{syncResult.employeesSkipped}</b> employees skipped</span>}
                {syncResult.photosUploaded > 0 && <span><b>{syncResult.photosUploaded}</b> photos uploaded</span>}
                {syncResult.photosFailed > 0 && <span style={{ color: C.amber }}><b>{syncResult.photosFailed}</b> photos failed</span>}
              </div>
            </div>
          )}

          {preview && (
            <div style={{ marginTop: '14px', borderTop: `1px solid ${C.border}`, paddingTop: '14px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>
                Preview — {preview.companies.length} companies, {preview.employeeCount} employees
              </div>

              {preview.companies.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '4px' }}>Companies</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {preview.companies.map(c => (
                      <span key={c.id} style={{ padding: '3px 9px', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '12px', fontSize: '11px', color: C.textSecondary }}>{c.name}</span>
                    ))}
                  </div>
                </div>
              )}

              {preview.employees.length > 0 && (
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: '7px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead style={{ position: 'sticky', top: 0, background: C.surfaceElevated }}>
                      <tr>
                        <th style={th}>Name</th>
                        <th style={th}>Company</th>
                        <th style={th}>Department</th>
                        <th style={th}>Job</th>
                        <th style={th}>Badge ID</th>
                        <th style={th}>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.employees.map(e => (
                        <tr key={e.odooId} style={{ borderTop: `1px solid ${C.border}` }}>
                          <td style={td}>{e.name}</td>
                          <td style={td}>{e.company || '—'}</td>
                          <td style={td}>{e.department || '—'}</td>
                          <td style={td}>{e.jobTitle || '—'}</td>
                          <td style={td} className="mono">{e.badgeId || '—'}</td>
                          <td style={td}>{e.phone || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '8px 12px', color: C.textPrimary };

const btnBase = (disabled) => ({
  padding: '8px 14px',
  borderRadius: '7px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  opacity: disabled ? 0.5 : 1,
});
const btnPrimary   = (d) => ({ ...btnBase(d), background: C.blue,            color: '#fff',          border: 'none' });
const btnSecondary = (d) => ({ ...btnBase(d), background: 'transparent',     color: C.textSecondary, border: `1px solid ${C.border}` });
const btnGhost     = (d) => ({ ...btnBase(d), background: 'transparent',     color: C.textMuted,     border: `1px solid ${C.border}` });
